import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import {
  FiArrowLeft, FiCalendar, FiMapPin, FiX, FiAlertCircle, FiPlus, FiMinus, FiEdit2, FiClock, FiCreditCard, FiArrowRight
} from "react-icons/fi";
import { BsPersonBadge, BsShieldCheck, BsEnvelope, BsIdCard } from "react-icons/bs";
import BusLayout from "../BusLayouts/BusLayout";
import BusBoardingPoints from "../BusBoardingPoints/BusBoardingPoints3";
import styles from "./BusSeatSelection3.module.css";

const BusSeatSelection3 = ({ mode, onNavigate, templateId, params, styleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const bus = params?.bus || location.state?.bus;
  const searchQuery = params?.searchQuery || location.state?.searchQuery;

  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#d97706", 
    "--secondary-color": styleConfig?.secondary || "#1e293b", 
  };

  const [seatLayoutData, setSeatLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerCount, setPassengerCount] = useState(1);
  const [showBoardingPoints, setShowBoardingPoints] = useState(false);
  const [selectedBoardingPoint, setSelectedBoardingPoint] = useState(null);
  const [selectedDroppingPoint, setSelectedDroppingPoint] = useState(null);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [fareSummary, setFareSummary] = useState({ baseFare: 0, convenienceFee: 0, total: 0 });

  const isUpdating = useRef(false);

  // Helper Formats
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
  };

  useEffect(() => {
    if (!bus || !searchQuery) {
      const target = mode === "builder" ? "bus-results" : "/template/bus/results";
      mode === "builder" ? onNavigate({ name: target }) : navigate(target);
      return;
    }
    fetchSeatLayout();
  }, [bus, searchQuery]);

  useEffect(() => {
    const initialDetails = Array.from({ length: passengerCount }, (_, i) => ({
      firstName: "", lastName: "", age: "", title: "Mr", 
      idProof: "", idProofNumber: "", // <--- ID PROOF ADDED
      email: user?.email || "", phone: user?.phoneNumber || "", 
      seatNumber: selectedSeats[i]?.id || "",
    }));
    setPassengerDetails(initialDetails);
  }, [user, passengerCount]);

  useEffect(() => {
    if (isUpdating.current || selectedSeats.length === 0) return;
    isUpdating.current = true;
    if (selectedSeats.length !== passengerCount) {
      setPassengerCount(selectedSeats.length);
      if (selectedSeats.length > passengerDetails.length) {
        const toAdd = selectedSeats.length - passengerDetails.length;
        const newPs = Array.from({ length: toAdd }, (_, i) => ({
          firstName: "", lastName: "", age: "", title: "Mr", idProof: "", idProofNumber: "",
          email: user?.email || "", phone: user?.phoneNumber || "",
          seatNumber: selectedSeats[passengerDetails.length + i]?.id || "",
        }));
        setPassengerDetails(prev => [...prev, ...newPs]);
      } else {
        setPassengerDetails(prev => prev.slice(0, selectedSeats.length));
      }
    }
    updateFareSummary();
    setTimeout(() => { isUpdating.current = false; }, 100);
  }, [selectedSeats]);

  const fetchSeatLayout = async () => {
    setLoading(true);
    try {
      const requestData = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        Source: searchQuery.fromStation, Destination: searchQuery.toStation,
        DateOfJourney: searchQuery.travelDate, InventoryType: bus.inventoryType, RouteScheduleId: bus.routeScheduleId,
      };
      const response = await apiService.post(`${config.GETBUSSEATS}/${bus.supplierId}`, requestData);
      if (response) setSeatLayoutData(response);
    } catch (err) { setError("Failed to load layout."); } finally { setLoading(false); }
  };

  const handleSeatSelect = (seat) => {
    if (!seat.available) return;
    const isSelected = selectedSeats.some(s => s.id === seat.id);
    setSelectedSeats(isSelected ? selectedSeats.filter(s => s.id !== seat.id) : [...selectedSeats, seat]);
  };

  const updateFareSummary = () => {
    const base = selectedSeats.reduce((t, s) => t + (parseFloat(s.totalPriceWithTaxes) || 0), 0);
    const fee = selectedSeats.reduce((t, s) => t + (parseFloat(s.convenienceFee) || 100), 0);
    setFareSummary({ baseFare: base, convenienceFee: fee, total: base + fee });
  };

  const handleProceedToPayment = () => {
    const data = { bus, searchQuery, selectedSeats, passengerCount, passengerDetails, selectedBoardingPoint, selectedDroppingPoint, totalAmount: fareSummary.total, baseFare: fareSummary.baseFare, convenienceFee: fareSummary.convenienceFee };
    if (mode === "builder") onNavigate({ name: "bus-payment", params: data });
    else navigate(mode === "preview" ? `/template-preview/${templateId}/bus-payment` : "/template/bus-payment", { state: data });
  };

  const areAllDetailsFilled = () => {
    return passengerDetails.every(p => 
      p.firstName && p.lastName && p.age && p.idProof && p.idProofNumber && p.email && p.phone
    );
  };

  return (
    <div className={styles.wrapper} style={themeVariables}>
      {/* Editorial Header */}
      <div className={styles.editorialHeader}>
        <div className={styles.container}>
          <div className={styles.headerFlex}>
            <div className={styles.headerLeft}>
              <button className={styles.backBtn} onClick={() => navigate(-1)}><FiArrowLeft /></button>
              <div>
                <span className={styles.topTag}>JOURNEY CONFIGURATION</span>
                <h1 className={styles.mainTitle}>Finalize Your <span className={styles.italic}>Travel</span></h1>
              </div>
            </div>
            <div className={styles.journeyBadge}>
              <strong>{searchQuery.fromStation} <FiArrowRight /> {searchQuery.toStation}</strong>
              <span><FiCalendar /> {formatDate(searchQuery.travelDate)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.contentGrid}>
          <div className={styles.mainContentArea}>
            {/* Bus Info Card */}
            <div className={styles.busInfoCard}>
              <div className={styles.operatorBox}>
                <h3>{bus.operatorName}</h3>
                <div className={styles.tagFlex}>
                    <span className={styles.busTypeTag}>{bus.busType}</span>
                    {bus.rtc && <span className={styles.rtcTag}>RTC</span>}
                </div>
              </div>
              <div className={styles.timeLine}>
                <div className={styles.timeBox}><strong>{bus.departureTime}</strong><span>{searchQuery.fromStation}</span></div>
                <div className={styles.duration}><FiClock /> {Math.floor(bus.durationInMins/60)}h {bus.durationInMins%60}m</div>
                <div className={styles.timeBox}><strong>{bus.arrivalTime}</strong><span>{searchQuery.toStation}</span></div>
              </div>
            </div>

            {/* Layout Card */}
            <div className={styles.seatCard}>
              <div className={styles.cardHeader}><h3>Seat Selection</h3></div>
              <div className={styles.layoutBody}>
                {loading ? <div className={styles.loader}>Curating layout...</div> : (
                  <BusLayout seatData={seatLayoutData} onSeatSelect={handleSeatSelect} selectedSeats={selectedSeats} />
                )}
              </div>
            </div>

            {/* Passenger Information */}
            {selectedSeats.length > 0 && (
              <div className={styles.passengerCard}>
                <div className={styles.cardHeader}><h3><BsPersonBadge /> Passenger Information</h3></div>
                <div className={styles.formsList}>
                  {passengerDetails.map((p, index) => (
                    <div key={index} className={styles.passengerRow}>
                      <div className={styles.rowHead}>
                        <span>Passenger {index + 1}</span>
                        <span className={styles.seatChip}>Seat {selectedSeats[index]?.id}</span>
                      </div>
                      <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                          <label>Title</label>
                          <select value={p.title} onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[index].title = e.target.value;
                              setPassengerDetails(updated);
                          }}><option>Mr</option><option>Ms</option><option>Mrs</option></select>
                        </div>
                        <div className={styles.formGroup}>
                          <label>First Name</label>
                          <input placeholder="Ex: John" value={p.firstName} onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[index].firstName = e.target.value;
                              setPassengerDetails(updated);
                          }} />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Last Name</label>
                          <input placeholder="Ex: Doe" value={p.lastName} onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[index].lastName = e.target.value;
                              setPassengerDetails(updated);
                          }} />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Age</label>
                          <input type="number" placeholder="25" value={p.age} onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[index].age = e.target.value;
                              setPassengerDetails(updated);
                          }} />
                        </div>
                        <div className={styles.formGroup}>
                          <label>ID Proof</label>
                          <select value={p.idProof} onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[index].idProof = e.target.value;
                              setPassengerDetails(updated);
                          }}>
                            <option value="">Select ID</option>
                            <option value="AADHAR">Aadhar Card</option>
                            <option value="PAN">PAN Card</option>
                            <option value="DL">Driving License</option>
                            <option value="PASSPORT">Passport</option>
                          </select>
                        </div>
                        <div className={styles.formGroup}>
                          <label>ID Number</label>
                          <input placeholder="Enter ID Number" value={p.idProofNumber} onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[index].idProofNumber = e.target.value;
                              setPassengerDetails(updated);
                          }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className={styles.contactInfo}>
                    <h4><BsEnvelope /> Confirmation Contact</h4>
                    <div className={styles.contactGrid}>
                        <div className={styles.formGroup}>
                            <label>Email Address</label>
                            <input placeholder="email@example.com" value={passengerDetails[0]?.email} onChange={(e) => {
                                setPassengerDetails(passengerDetails.map(p => ({...p, email: e.target.value})));
                            }} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Phone Number</label>
                            <input placeholder="9999999999" value={passengerDetails[0]?.phone} onChange={(e) => {
                                setPassengerDetails(passengerDetails.map(p => ({...p, phone: e.target.value})));
                            }} />
                        </div>
                    </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className={styles.sidebar}>
            <div className={styles.summaryCard}>
              <h3>Premium Summary</h3>
              <div className={styles.summarySec}>
                <label><FiMapPin /> Boarding Details</label>
                {selectedBoardingPoint ? (
                  <div className={styles.pointSelected}>
                    <strong>{selectedBoardingPoint.pointName}</strong>
                    <button onClick={() => setShowBoardingPoints(true)}><FiEdit2 /></button>
                  </div>
                ) : (
                  <button className={styles.actionLink} onClick={() => setShowBoardingPoints(true)}>Select Points</button>
                )}
              </div>

              <div className={styles.summarySec}>
                <label><BsPersonBadge /> Selected Seats</label>
                <div className={styles.seatTags}>
                  {selectedSeats.map(s => <span key={s.id}>{s.id}</span>)}
                </div>
              </div>

              <div className={styles.fareBox}>
                <div className={styles.fareRow}><span>Base Fare</span><strong>₹{fareSummary.baseFare}</strong></div>
                <div className={styles.fareRow}><span>Fees</span><strong>₹{fareSummary.convenienceFee}</strong></div>
                <div className={styles.totalRow}><span>Total Payable</span><strong>₹{fareSummary.total}</strong></div>
              </div>

              <button 
                className={styles.payBtn} 
                disabled={selectedSeats.length === 0 || !areAllDetailsFilled() || !selectedBoardingPoint}
                onClick={handleProceedToPayment}
              >
                <FiCreditCard /> PROCEED TO CHECKOUT
              </button>
              <div className={styles.secureBagde}><BsShieldCheck /> Secure Nomad Encryption</div>
            </div>
          </aside>
        </div>
      </div>

      {showBoardingPoints && (
        <BusBoardingPoints bus={bus} onClose={() => setShowBoardingPoints(false)} onSubmit={(b, d) => { setSelectedBoardingPoint(b); setSelectedDroppingPoint(d); setShowBoardingPoints(false); }} />
      )}
    </div>
  );
};

export default BusSeatSelection3;